import React from 'react';

import * as cookies from 'lib/cookies';

import {
  DEFAULT_LOCALE,
  getMessages,
  hasLocaleMessages,
  type Locale,
  loadMessages,
  type MessageParams,
  type Messages,
  setLocaleMessages,
  translate,
  translateFromMessages,
} from './core';

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: MessageParams) => string;
};

const I18nContext = React.createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => undefined,
  t: (key, params) => translate(DEFAULT_LOCALE, key, params),
});

type Props = {
  children: React.ReactNode;
  initialLocale?: Locale;
  initialMessages?: Messages;
};

export function I18nProvider({ children, initialLocale = DEFAULT_LOCALE, initialMessages }: Props) {
  const registeredInitialMessagesRef = React.useRef<Messages | undefined>();

  if (initialMessages && registeredInitialMessagesRef.current !== initialMessages) {
    setLocaleMessages(initialLocale, initialMessages);
    registeredInitialMessagesRef.current = initialMessages;
  }

  const [ locale, setLocaleState ] = React.useState<Locale>(initialLocale);
  const [ messages, setMessages ] = React.useState<Messages>(() => getMessages(initialLocale));

  React.useEffect(() => {
    window.document.documentElement.lang = locale;
  }, [ locale ]);

  React.useEffect(() => {
    let isActive = true;

    if (hasLocaleMessages(locale)) {
      setMessages(getMessages(locale));
      return undefined;
    }

    void loadMessages(locale).then((msgs) => {
      if (isActive) {
        setMessages(msgs);
      }
    }).catch(() => undefined);

    return () => {
      isActive = false;
    };
  }, [ locale ]);

  const setLocale = React.useCallback((nextLocale: Locale) => {
    void loadMessages(nextLocale).then(() => {
      setLocaleState(nextLocale);
      cookies.set(cookies.NAMES.LOCALE, nextLocale, {
        expires: 365,
        sameSite: 'lax',
        secure: window.location.protocol === 'https:',
      });
    }).catch(() => undefined);
  }, []);

  const t = React.useCallback((key: string, params?: MessageParams) => translateFromMessages(messages, locale, key, params), [ locale, messages ]);

  const value = React.useMemo(() => ({
    locale,
    setLocale,
    t,
  }), [ locale, setLocale, t ]);

  return (
    <I18nContext.Provider value={ value }>
      { children }
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return React.useContext(I18nContext);
}
